import Schema from './Normalize'
import { sphericalToCartesian } from '../../utils/conversions'

const countElements = state => {
    const { stops, panoramas } = state
    let total = { byStop: {} }
    stops.allIds.forEach(stopId => {
        const stop = stops.byId[stopId]
        const panoId = stop.main_media_id
        const pano = panoramas.byId[stop.panoramas.find(pid => pid === panoId)]
        if (pano) {
            const { hotspots = [], positionalMedia = [], triggers = [] } = pano

            total.byStop[stopId] = [
                ...hotspots.map(id => ({ id, type: 'hotspots' })),
                ...positionalMedia.map(id => ({ id, type: 'positionalMedia' })),
                ...triggers.map(id => ({ id, type: 'triggers' })),
            ]
        }
    })
    return total
}

const normalizeVideoSource = mediaItem =>
    (!mediaItem.external_url || String(mediaItem.external_url).length < 1) &&
    (mediaItem.videoid && mediaItem.videoid.length > 0)
        ? `https://www.youtube.com/watch?v=${mediaItem.videoid}`
        : mediaItem.external_url

const renameTypes = type => {
    switch (type) {
        case 'tooltip':
            return 'text'

        default:
            return type
    }
}

/**
 * Calculate center of Array of {x,y,z} by sum / n
 * https://en.wikipedia.org/wiki/Centroid
 * @param Array points
 */
const calculateCenterFromPoints = points => {
    if (points.length == 1) {
        return points[0]
    }
    let sumX = 0,
        sumY = 0,
        sumZ = 0
    for (var i = 0; i < points.length; i++) {
        sumX += points[i].x
        sumY += points[i].y
        sumZ += points[i].z
    }
    return { x: sumX / points.length, y: sumY / points.length, z: sumZ / points.length }
}

export const get2DContextCoordinates = points => {
    const centerPoint = calculateCenterFromPoints(points)

    return (width, height) => ({ x: centerPoint.x * width, y: centerPoint.y * height, z: 0 })
}

const normalizeCoordinates = coords => {
    let nCoords = calculateCenterFromPoints(coords)
    return sphericalToCartesian({ phi: nCoords.x, theta: nCoords.y, r: 2 })
}

// Used to situate element in 3d place for placement in panoplayer
const normalizeCoordinatesTo3DCartesian = coords => {
    let nCoords = calculateCenterFromPoints(coords)
    // 'r' is approximation of radius of panoplayer sphere.  Should be set to constant
    // 'r' is being estimated on the low end to give smaller z value to set triggers in front of positional media, for now
    return sphericalToCartesian({ phi: nCoords.x, theta: nCoords.y, r: 2 })
}

export const normalizeData = data => {
    const schema = new Schema()
    // Sets root as data field
    schema.addNode('root', { id: 'id', root: true, fields: ['data'] })

    // Look in data array. Collect panoramas, photos, and videos values and group by stopid as key.  Rename to 'stops'.
    // Results in allIds object as array of ids, along with byId object with each Id mapped to collected value
    // NOTE: does 'field' param actually do anything?  doesn't seem to
    schema.addNode('data', { id: 'stopid', fields: ['panoramas', 'photos', 'videos'] }, { rename: 'stops' })

    // Creates collection object with similar to structure as above.
    // Resolve predicate is applied to the fields corresponding to its given key
    schema.addNode(
        'panoramas',
        { id: 'id', fields: ['triggers', 'objs', 'hotspots'] },
        {
            resolve: {
                hotspots: slice => {
                    return slice.hotspots.filter(
                        hotspot => !hotspot.media_items || hotspot.media_items[0].item_type !== '360 video'
                    )
                },
                triggers: slice => {
                    return slice.hotspots.filter(
                        hotspot => hotspot.media_items && hotspot.media_items[0].item_type === '360 video'
                    )
                },
                objs: slice => {
                    let filteredObjs = slice.objs.filter(obj => obj.media_item.item_type !== 'photo')
                    // override positional media titles and autoplay
                    let enumerationByOwner = {}
                    return filteredObjs.map(obj => {
                        enumerationByOwner[obj.owner_id] = enumerationByOwner[obj.owner_id]
                            ? ++enumerationByOwner[obj.owner_id]
                            : 1
                        obj.title = 'Video ' + enumerationByOwner[obj.owner_id]
                        obj.autoplay = enumerationByOwner[obj.owner_id] === 1 && obj.autoplay === 1
                        return obj
                    })
                },
            },
        }
    )

    // Creates collection object with similar to structure as above.
    // filter predicate removes items according to truthiness of return
    // transform predicate allows for merging of additional derived fields
    schema.addNode(
        'hotspots',
        { id: 'id', fields: ['media_items'] },
        {
            filter: hotspot => hotspot.coordinates && hotspot.coordinates.length > 0 && hotspot.mediaItems.length > 0,
            transform: hotspot => {
                return {
                    ...hotspot,
                    center: normalizeCoordinates(hotspot.coordinates),
                    cartesian3DCoords: normalizeCoordinatesTo3DCartesian(hotspot.coordinates),
                }
            },
            /**
             * This resolve is to fix FS18-1684, tooltip hotspots not present.
             * Tooltips all have item_id = 0, this sets their item_id to the hotspot id.
             * This could break if in the future hotspots have multiple medias
             */
            resolve: {
                media_items: slice => {
                    return slice.media_items.map(item => {
                        if (item.item_id === 0 && item.item_type === 'tooltip') {
                            item.item_id = slice.id
                            if (!item.title) {
                                item.title = slice.title
                            }
                            return item
                        } else {
                            return item
                        }
                    })
                },
            },
        }
    )
    schema.addNode(
        'triggers',
        { id: 'id', fields: ['media_items'] },
        {
            filter: trigger => trigger.coordinates && trigger.coordinates.length > 0,
            transform: trigger => {
                return {
                    ...trigger,
                    center: normalizeCoordinates(trigger.coordinates),
                    cartesian3DCoords: normalizeCoordinatesTo3DCartesian(trigger.coordinates),
                }
            },
        }
    )

    schema.addNode(
        'media_items',
        { id: 'item_id', fields: ['media_items', 'video'] },
        {
            rename: 'mediaItems',
            transform: mI => ({
                ...mI,
                external_url: normalizeVideoSource(mI),
                item_type: renameTypes(mI.item_type),
            }),
        }
    )
    schema.addNode('video', { id: 'videoid' }, { rename: 'videoMedia', parent: 'media_items' })

    schema.addNode(
        'objs',
        { id: 'id', fields: ['media_item'] },
        {
            rename: 'positionalMedia',
            transform: pm => ({
                ...pm,
                center: calculateCenterFromPoints(pm.position),
            }),
        }
    )
    schema.addNode('media_item', { id: 'item_id', fields: ['photo', 'video'] }, { rename: 'positionalMediaItems' })
    schema.addNode('video', { id: 'videoid', fields: ['renditions'] }, { rename: 'videoMain', parent: 'media_item' })

    schema.addNode(
        'photo',
        { id: 'photoid', fields: ['triggers', 'objs', 'hotspots'] },
        {
            rename: 'photoMain',
            resolve: {
                hotspots: slice => {
                    return slice.hotspots.filter(
                        hotspot => !hotspot.media_items || hotspot.media_items[0].item_type !== '360 video'
                    )
                },
                triggers: slice => {
                    return slice.hotspots.filter(
                        hotspot => hotspot.media_items && hotspot.media_items[0].item_type === '360 video'
                    )
                },
            },
        }
    )

    schema.addNode(
        'photos',
        { id: 'photoid', fields: ['triggers', 'objs', 'hotspots'] },
        {
            rename: 'photoMain',
            resolve: {
                hotspots: slice => {
                    return slice.hotspots.filter(
                        hotspot => !hotspot.media_items || hotspot.media_items[0].item_type !== '360 video'
                    )
                },
                triggers: slice => {
                    return slice.hotspots.filter(
                        hotspot => hotspot.media_items && hotspot.media_items[0].item_type === '360 video'
                    )
                },
            },
        }
    )

    schema.addNode('videos', { id: 'videoid', fields: ['renditions'] }, { rename: 'videoMain' })
    schema.addNode('renditions', { id: 'id' })

    schema.addNode('audio', { id: 'audioid' })

    // Generates normalization by id
    const normal = schema.normalize([data])

    // Calculates and merges in metadata
    const totalElements = countElements(normal)

    const final = {
        ...normal,
        meta: {
            totalElements,
        },
    }

    // Returns final data structure for use as store
    return final
}
